import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import tr from 'date-fns/locale/tr';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = {
  'tr': tr,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const messages = {
  allDay: 'Tüm gün',
  previous: 'Geri',
  next: 'İleri',
  today: 'Bugün',
  month: 'Ay',
  week: 'Hafta',
  day: 'Gün',
  agenda: 'Gündem',
  date: 'Tarih',
  time: 'Saat',
  event: 'Etkinlik',
  noEventsInRange: 'Bu aralıkta etkinlik yok.',
};

const CompanyCalendar = ({ companyId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('admin_token')}` };
      
      const [apptsRes, shootsRes] = await Promise.all([
        axios.get(`/api/admin/companies/${companyId}/appointments`, { headers }),
        axios.get(`/api/admin/companies/${companyId}/shooting-schedules`, { headers })
      ]);

      const formattedAppts = apptsRes.data.map(appt => {
        const start = new Date(appt.appointment_date);
        const end = new Date(start.getTime() + 60 * 60 * 1000); // 1 saatlik randevu
        return {
          id: `appt_${appt.id}`,
          title: `📅 Randevu: ${appt.title}`,
          start,
          end,
          type: 'appointment',
          status: appt.status,
          description: appt.description,
          location: ''
        };
      });

      const formattedShoots = shootsRes.data.map(shoot => {
        const start = new Date(shoot.schedule_date);
        const end = new Date(start.getTime() + 120 * 60 * 1000); // 2 saatlik çekim
        return {
          id: `shoot_${shoot.id}`,
          title: `📸 Çekim: ${shoot.title}`,
          start,
          end,
          type: 'shooting',
          status: 'completed',
          description: '',
          location: shoot.location
        };
      });

      setEvents([...formattedAppts, ...formattedShoots]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [companyId]);

  const eventPropGetter = (event) => {
    let backgroundColor = '#3B82F6'; // Mavi (Randevu)
    if (event.type === 'shooting') {
      backgroundColor = '#10B981'; // Yeşil (Çekim)
    } else if (event.status === 'cancelled') {
      backgroundColor = '#EF4444'; // Kırmızı (İptal)
    } else if (event.status === 'completed') {
      backgroundColor = '#059669'; // Koyu Yeşil (Tamamlanan Randevu)
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        color: 'white',
        border: 'none',
        display: 'block',
        fontSize: '12px',
        fontWeight: 500
      }
    };
  };

  return (
    <div style={{ color: '#fff' }}>
      <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '16px', fontWeight: 600 }}>📅 Entegre Randevu ve Çekim Takvimi</h3>
      
      {loading ? (
        <p>Yükleniyor...</p>
      ) : (
        <div style={{ height: '500px', background: 'rgba(26,26,36,0.4)', padding: '20px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            culture="tr"
            messages={messages}
            eventPropGetter={eventPropGetter}
            onSelectEvent={event => setSelectedEvent(event)}
            style={{ height: '100%', color: '#E5E7EB' }}
          />
        </div>
      )}

      {selectedEvent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'rgba(17,17,24,0.95)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '16px',
            padding: '24px',
            width: '400px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
            color: '#fff'
          }}>
            <h3 style={{ marginTop: 0, color: selectedEvent.type === 'appointment' ? '#60A5FA' : '#34D399' }}>
              {selectedEvent.type === 'appointment' ? '📅 Randevu Detayı' : '📸 Çekim Detayı'}
            </h3>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '4px' }}>Başlık</div>
              <div style={{ fontSize: '15px', fontWeight: 600 }}>{selectedEvent.title.replace(/📅 Randevu: |📸 Çekim: /, '')}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '4px' }}>Zaman</div>
              <div style={{ fontSize: '14px' }}>
                {selectedEvent.start.toLocaleString('tr-TR')} - {selectedEvent.end.toLocaleString('tr-TR')}
              </div>
            </div>
            {selectedEvent.location && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '4px' }}>Konum</div>
                <div style={{ fontSize: '14px' }}>📍 {selectedEvent.location}</div>
              </div>
            )}
            {selectedEvent.description && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '4px' }}>Açıklama</div>
                <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{selectedEvent.description}</div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button 
                onClick={() => setSelectedEvent(null)}
                style={{
                  padding: '8px 16px',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyCalendar;
